import React, { useEffect, useMemo, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { CalendarClock, CalendarOff, Loader2, Plus, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { api, errorMessage } from "@/lib/api";
import { useMeta } from "@/hooks/useMeta";
import { fmtDate } from "@/lib/format";
import { Btn, Card, EmptyState, PageHeader } from "@/components/dashboard/Ui";
import { AvailabilityCalendar } from "@/components/dashboard/AvailabilityCalendar";

const DAYS = [
  ["mon", "Monday"], ["tue", "Tuesday"], ["wed", "Wednesday"], ["thu", "Thursday"],
  ["fri", "Friday"], ["sat", "Saturday"], ["sun", "Sunday"],
];

function SpecialtyDot({ specialty }) {
  const { data: meta } = useMeta();
  const spec = specialty && meta?.specialties?.[specialty];
  if (!spec) return null;
  return (
    <span className="inline-flex items-center gap-1.5 rounded-full px-2 py-0.5 text-[11px] font-semibold"
      style={{ backgroundColor: `${spec.color}1A`, color: spec.color }}>
      <span className="h-2 w-2 rounded-full" style={{ backgroundColor: spec.color }} />
      {spec.label}
    </span>
  );
}

/** Editable list of HH:MM blocks for one weekday. */
function DayRow({ label, slots, onChange }) {
  const add = () => onChange([...slots, { start: "09:00", end: "17:00" }]);
  const update = (i, key, value) => onChange(slots.map((s, j) => (j === i ? { ...s, [key]: value } : s)));
  const remove = (i) => onChange(slots.filter((_, j) => j !== i));
  return (
    <div className="flex flex-col gap-2 border-b border-border py-3 last:border-0 sm:flex-row sm:items-start">
      <div className="w-32 shrink-0 pt-1.5 text-sm font-semibold text-foreground">{label}</div>
      <div className="flex-1">
        {slots.length === 0 ? (
          <p className="py-1.5 text-sm text-muted-foreground">Not working</p>
        ) : (
          <div className="space-y-2">
            {slots.map((s, i) => (
              <div key={i} className="flex items-center gap-2">
                <input type="time" value={s.start} onChange={(e) => update(i, "start", e.target.value)}
                  className="emerge-input !h-9 !w-32 text-sm" aria-label={`${label} start`} />
                <span className="text-muted-foreground">to</span>
                <input type="time" value={s.end} onChange={(e) => update(i, "end", e.target.value)}
                  className="emerge-input !h-9 !w-32 text-sm" aria-label={`${label} end`} />
                <button type="button" onClick={() => remove(i)} aria-label={`Remove ${label} block`}
                  className="rounded-[8px] p-1.5 text-muted-foreground hover:bg-destructive/10 hover:text-destructive">
                  <Trash2 className="h-4 w-4" />
                </button>
              </div>
            ))}
          </div>
        )}
        <button type="button" onClick={add}
          className="mt-2 inline-flex items-center gap-1 text-xs font-semibold text-primary hover:underline">
          <Plus className="h-3.5 w-3.5" /> Add a block
        </button>
      </div>
    </div>
  );
}

export default function AvailabilityPage() {
  const qc = useQueryClient();
  const [selected, setSelected] = useState(null);
  const [weekly, setWeekly] = useState({});
  const [dirty, setDirty] = useState(false);
  const [excDate, setExcDate] = useState("");
  const [excUnavailable, setExcUnavailable] = useState(true);
  const [excStart, setExcStart] = useState("09:00");
  const [excEnd, setExcEnd] = useState("13:00");
  const [excNote, setExcNote] = useState("");

  const { data: overview, isLoading } = useQuery({
    queryKey: ["availability-overview"],
    queryFn: async () => (await api.get("/availability/overview")).data.items,
  });

  const therapists = useMemo(() => overview || [], [overview]);
  useEffect(() => {
    if (!selected && therapists.length) setSelected(therapists[0].user_id);
  }, [therapists, selected]);

  const { data: detail } = useQuery({
    queryKey: ["availability", selected],
    queryFn: async () => (await api.get(`/availability/${selected}`)).data,
    enabled: !!selected,
  });

  useEffect(() => {
    if (detail) { setWeekly(detail.weekly || {}); setDirty(false); }
  }, [detail]);

  const current = therapists.find((t) => t.user_id === selected);

  const saveWeekly = useMutation({
    mutationFn: async () => (await api.put(`/availability/${selected}/weekly`, { weekly })).data,
    onSuccess: () => {
      toast.success(`Weekly hours saved for ${current?.name}`);
      setDirty(false);
      qc.invalidateQueries({ queryKey: ["availability-overview"] });
      qc.invalidateQueries({ queryKey: ["availability", selected] });
      qc.invalidateQueries({ queryKey: ["availability-slots"] });
    },
    onError: (e) => toast.error(errorMessage(e)),
  });

  const addException = useMutation({
    mutationFn: async () =>
      (await api.post(`/availability/${selected}/exceptions`, {
        date: excDate,
        unavailable: excUnavailable,
        slots: excUnavailable ? [] : [{ start: excStart, end: excEnd }],
        note: excNote || null,
      })).data,
    onSuccess: () => {
      toast.success("Exception saved");
      setExcDate(""); setExcNote("");
      qc.invalidateQueries({ queryKey: ["availability", selected] });
      qc.invalidateQueries({ queryKey: ["availability-slots"] });
    },
    onError: (e) => toast.error(errorMessage(e)),
  });

  const removeException = useMutation({
    mutationFn: async (date) => (await api.delete(`/availability/${selected}/exceptions/${date}`)).data,
    onSuccess: () => {
      toast.success("Exception removed");
      qc.invalidateQueries({ queryKey: ["availability", selected] });
      qc.invalidateQueries({ queryKey: ["availability-slots"] });
    },
    onError: (e) => toast.error(errorMessage(e)),
  });

  const totalBlocks = useMemo(
    () => DAYS.reduce((n, [k]) => n + (weekly[k]?.length || 0), 0),
    [weekly]
  );

  if (isLoading) {
    return <div className="flex justify-center py-16"><Loader2 className="h-6 w-6 animate-spin text-primary" /></div>;
  }

  return (
    <div className="space-y-6" data-testid="availability-page">
      <PageHeader
        title="Therapist availability"
        description="Set the normal working week for each therapist, then add exceptions for leave or one-off sessions. Appointments can only be booked inside these hours."
      />

      <div className="grid gap-6 lg:grid-cols-[260px_1fr]">
        {/* Therapist list */}
        <Card className="h-fit p-2">
          {therapists.length === 0 ? (
            <EmptyState icon={CalendarClock} title="No active therapists" description="Invite your team first." />
          ) : (
            <ul className="space-y-1" data-testid="availability-therapist-list">
              {therapists.map((t) => (
                <li key={t.user_id}>
                  <button
                    type="button"
                    onClick={() => setSelected(t.user_id)}
                    data-testid={`availability-therapist-${t.user_id}`}
                    className={[
                      "w-full rounded-[8px] px-3 py-2.5 text-left transition",
                      t.user_id === selected ? "bg-primary/10 ring-1 ring-primary/40" : "hover:bg-secondary",
                    ].join(" ")}
                  >
                    <span className="flex items-center gap-2">
                      <span className="h-2.5 w-2.5 shrink-0 rounded-full" style={{ backgroundColor: t.color || "#94a3b8" }} />
                      <span className="truncate text-sm font-semibold text-foreground">{t.name}</span>
                    </span>
                    <span className="mt-1 flex items-center gap-2">
                      <SpecialtyDot specialty={t.specialty} />
                      {!t.configured && <span className="text-[11px] text-destructive">No hours set</span>}
                    </span>
                  </button>
                </li>
              ))}
            </ul>
          )}
        </Card>

        <div className="space-y-6">
          {/* Weekly pattern */}
          <Card className="p-5">
            <div className="mb-2 flex flex-wrap items-center justify-between gap-3">
              <div>
                <h2 className="text-lg font-bold text-foreground">Normal week</h2>
                <p className="text-sm text-muted-foreground">
                  {current?.name ? `Repeats every week for ${current.name}.` : "Select a therapist."} {totalBlocks} block{totalBlocks === 1 ? "" : "s"}.
                </p>
              </div>
              <Btn onClick={() => saveWeekly.mutate()} disabled={!dirty || saveWeekly.isPending} data-testid="availability-save-weekly">
                {saveWeekly.isPending && <Loader2 className="animate-spin" />} Save weekly hours
              </Btn>
            </div>
            <div className="mt-3">
              {DAYS.map(([key, label]) => (
                <DayRow
                  key={key}
                  label={label}
                  slots={weekly[key] || []}
                  onChange={(slots) => { setWeekly({ ...weekly, [key]: slots }); setDirty(true); }}
                />
              ))}
            </div>
          </Card>

          {/* Exceptions */}
          <Card className="p-5">
            <h2 className="text-lg font-bold text-foreground">Exceptions</h2>
            <p className="text-sm text-muted-foreground">
              Override a single date - leave, a holiday, or extra hours. An exception always wins over the normal week.
            </p>

            <div className="mt-4 grid gap-3 rounded-[10px] border border-border bg-secondary/40 p-4 sm:grid-cols-2">
              <div>
                <label htmlFor="exc-date" className="mb-1.5 block text-xs font-semibold uppercase tracking-wide text-muted-foreground">Date</label>
                <input id="exc-date" type="date" className="emerge-input !h-10 text-sm" value={excDate} onChange={(e) => setExcDate(e.target.value)} data-testid="availability-exception-date" />
              </div>
              <div>
                <span className="mb-1.5 block text-xs font-semibold uppercase tracking-wide text-muted-foreground">Type</span>
                <div className="flex gap-4 pt-2 text-sm">
                  <label className="flex items-center gap-2">
                    <input type="radio" checked={excUnavailable} onChange={() => setExcUnavailable(true)} className="h-4 w-4 accent-[#c2493a]" /> Unavailable
                  </label>
                  <label className="flex items-center gap-2">
                    <input type="radio" checked={!excUnavailable} onChange={() => setExcUnavailable(false)} className="h-4 w-4 accent-[#2f5d83]" /> Custom hours
                  </label>
                </div>
              </div>
              {!excUnavailable && (
                <div className="flex items-end gap-2 sm:col-span-2">
                  <input type="time" value={excStart} onChange={(e) => setExcStart(e.target.value)} className="emerge-input !h-10 !w-32 text-sm" aria-label="Exception start" />
                  <span className="pb-2.5 text-muted-foreground">to</span>
                  <input type="time" value={excEnd} onChange={(e) => setExcEnd(e.target.value)} className="emerge-input !h-10 !w-32 text-sm" aria-label="Exception end" />
                </div>
              )}
              <div className="sm:col-span-2">
                <label htmlFor="exc-note" className="mb-1.5 block text-xs font-semibold uppercase tracking-wide text-muted-foreground">Reason (optional)</label>
                <input id="exc-note" className="emerge-input !h-10 text-sm" value={excNote} onChange={(e) => setExcNote(e.target.value)} maxLength={300} placeholder="Annual leave" />
              </div>
              <div className="sm:col-span-2">
                <Btn onClick={() => addException.mutate()} disabled={!excDate || addException.isPending} data-testid="availability-add-exception">
                  {addException.isPending && <Loader2 className="animate-spin" />} Add exception
                </Btn>
              </div>
            </div>

            <div className="mt-4">
              {(detail?.exceptions || []).length === 0 ? (
                <p className="py-3 text-sm text-muted-foreground">No exceptions for this therapist.</p>
              ) : (
                <ul className="divide-y divide-border" data-testid="availability-exception-list">
                  {detail.exceptions.map((e) => (
                    <li key={e.date} className="flex items-center justify-between gap-3 py-3">
                      <div className="min-w-0">
                        <p className="flex items-center gap-2 text-sm font-semibold text-foreground">
                          {e.unavailable ? <CalendarOff className="h-4 w-4 text-destructive" /> : <CalendarClock className="h-4 w-4 text-primary" />}
                          {fmtDate(`${e.date}T00:00:00Z`)}
                        </p>
                        <p className="truncate text-sm text-muted-foreground">
                          {e.unavailable ? "Unavailable all day" : e.slots.map((s) => `${s.start} - ${s.end}`).join(", ")}
                          {e.note ? ` · ${e.note}` : ""}
                        </p>
                      </div>
                      <button type="button" onClick={() => removeException.mutate(e.date)}
                        aria-label={`Remove exception on ${e.date}`}
                        className="rounded-[8px] p-2 text-muted-foreground hover:bg-destructive/10 hover:text-destructive">
                        <Trash2 className="h-4 w-4" />
                      </button>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          </Card>
        </div>
      </div>

      <AvailabilityCalendar />
    </div>
  );
}
